import type { Permission, SessionUser } from '@jmc/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { api, ApiRequestError } from './api.js';

interface AuthValue {
  user: SessionUser | null;
  isLoading: boolean;
  can: (permission: Permission) => boolean;
  signIn: (username: string, password: string) => Promise<SessionUser>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        const result = await api<{ user: SessionUser }>('/auth/me');
        return result.user;
      } catch (error) {
        // 401 is the normal "not signed in" answer, not a failure worth
        // retrying or surfacing.
        if (error instanceof ApiRequestError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const user = data ?? null;

  const signIn = useCallback(
    async (username: string, password: string) => {
      const result = await api<{ user: SessionUser }>('/auth/login', {
        method: 'POST',
        body: { username, password },
      });
      queryClient.setQueryData(['session'], result.user);
      return result.user;
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    queryClient.setQueryData(['session'], null);
    // Nothing cached should outlive the session it was fetched under.
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['session'] });
  }, [queryClient]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      isLoading,
      // The server checks this again on every request; this is only to decide
      // what to render.
      can: (permission) => user?.permissions.includes(permission) ?? false,
      signIn,
      signOut,
      refresh,
    }),
    [user, isLoading, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
